/// <reference path="../groupthink-js.d.ts" />
import Axios, { AxiosRequestConfig } from 'axios';
import { SWRResponse } from 'swr';
import { isBrowser } from './platform';

const DEFAULTS = {
  serverURL: 'https://api.groupthink.com',
  groupthinkClientName: 'packages/groupthink-js',
  groupthinkClientType: '',
  useCredentials: false,
  throwOnError: false,
};

if (process.env.NEXT_PUBLIC_BACKEND_URL) {
  DEFAULTS.serverURL = process.env.NEXT_PUBLIC_BACKEND_URL;
}

if (process.env.EXPO_PUBLIC_BACKEND_URL) {
  DEFAULTS.serverURL = process.env.EXPO_PUBLIC_BACKEND_URL;
}

if (
  process.env.NEXT_PUBLIC_AXIOS_THROW_ON_ERROR === 'true' ||
  process.env.EXPO_PUBLIC_AXIOS_THROW_ON_ERROR === 'true'
) {
  DEFAULTS.throwOnError = true;
}

if (isBrowser()) {
  DEFAULTS.useCredentials = true;
  DEFAULTS.groupthinkClientType = 'web';
} else {
  DEFAULTS.groupthinkClientType = 'app';
}

export const axios = Axios.create({
  baseURL: DEFAULTS.serverURL,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'X-Groupthink-Client': DEFAULTS.groupthinkClientName,
  },
  withCredentials: DEFAULTS.useCredentials,
  withXSRFToken: true,
});

if (process.env.NODE_ENV === 'development') {
  console.debug(
    'Groupthink API Client initialized with defaults:',
    JSON.stringify(DEFAULTS, null, 2)
  );
}

/**
 * Fetch data from the API.
 * Requires the client to be initialized.
 *
 * @param url
 * @returns The data returned by the API.
 */
export const fetcher = (url: string) => {
  return axios
    .get(url)
    .then((res) => res.data)
    .catch((error) => {
      if (error.response.status === 401 && isBrowser()) {
        window.location.assign('/signin');
      }

      if (DEFAULTS.throwOnError) {
        throw error;
      }
    });
};

/**
 * Mutate data on the API.
 * Requires the client to be initialized.
 *
 * @param url
 * @param options
 */
export const mutateInternal = async <RouteName>(
  url: string,
  options: {
    method?: AxiosRequestConfig['method'];
    payload?: Record<string, unknown> | Groupthink.RequestPayload<RouteName>;
  }
) => {
  const { method = 'POST', payload } = options;

  try {
    await axios(url, {
      method,
      data: payload,
    });
  } catch (error) {
    if (DEFAULTS.throwOnError) {
      throw error;
    }
  }
};

/**
 * Make an API request.
 * Requires the client to be initialized.
 *
 * @param url
 * @param mutate
 * @param method
 * @param options
 * @returns
 */
export const apiRequest = async <RouteName = void>(
  url: string,
  mutate?: SWRResponse['mutate'],
  method?: AxiosRequestConfig['method'],
  options?: {
    setErrors?: Groupthink.ErrorsCallback;
    setLoading?: (loading: boolean) => void;
    payload?: Record<string, unknown> | Groupthink.RequestPayload<RouteName> | FormData;
    headers?: Record<string, string>;
    onSuccess?: Groupthink.OnSuccessCallback | Groupthink.OnSuccessResponseDataCallback<RouteName>;
  }
) => {
  const { setErrors, setLoading, payload, onSuccess, headers } = options || {};

  setErrors?.({});
  setLoading?.(true);
  return axios(url, {
    method,
    data: payload,
    headers,
  })
    .then((res) => {
      mutate?.().then(() => {
        setLoading?.(false);
        onSuccess?.(res.data?.data ?? res.data);
      });
    })
    .catch((error) => {
      // TODO: We should be able to determine which errors are expected to be returned by a RouteName
      setErrors?.(error.response.data.errors ?? { error });
      setLoading?.(false);
    });
};

/**
 * Configure the axios instance to use an Authorization header.
 */
export const configureAxiosBaseUrl = (url: string) => {
  axios.defaults.baseURL = url;
};

/**
 * Configure the axios instance to use an Authorization header.
 */
export const configureAuthorizationHeader = (token: string) => {
  axios.defaults.headers.Authorization = `Bearer ${token}`;
};

/**
 * Clears the Authorization header from the axios instance.
 */
export const clearAuthorizationHeader = () => {
  delete axios.defaults.headers.Authorization;
};
